/**
 * AdultPresets - Skunked and Stripped Track Presets & Layout Engine
 * Provides curated strip & timed dare board presets (Warm-Up Strip, High Heat, Sensual Gauntlet)
 * and custom layout CRUD management persisted in IndexedDB.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';
import { LayoutSanitizer, MAX_CUSTOM_LAYOUTS } from './LayoutSanitizer.js';

export const ADULT_CUSTOM_LAYOUTS_KEY = 'cc_adult_custom_layouts';

export const ADULT_PRESET_KEYS = Object.freeze({
  STANDARD: 'standard',
  HIGH_HEAT: 'highHeat',
  SENSUAL_GAUNTLET: 'sensualGauntlet'
});

export const ADULT_PRESETS_121 = Object.freeze({
  standard: {
    key: 'standard',
    name: 'Warm-Up Strip',
    badge: '👗 Gentle Strip',
    icon: '👗',
    description: 'A playful and flirtatious intro. Balanced strip spaces with chances to gain clothing back and occasional sensual touch dares.',
    tags: ['Strip', 'Flirty', 'Playful'],
    dareSpaces: 14,
    config: {
      optLoseClothing: true,
      optLoseClothingCount: 2,
      optLoseClothingPegs: 1,
      optLoseClothing1Hole: 15,
      optLoseClothing2Hole: 75,

      optGainClothing: true,
      optGainClothingCount: 2,
      optGainClothingPegs: 1,
      optGainClothing1Hole: 25,
      optGainClothing2Hole: 85,

      optPickWhoLosesClothing: true,
      optPickWhoLosesClothingCount: 2,
      optPickWhoLosesClothingPegs: 1,
      optPickWhoLosesClothing1Hole: 35,
      optPickWhoLosesClothing2Hole: 95,

      optRightLosesClothing: true,
      optRightLosesClothingCount: 2,
      optRightLosesClothingPegs: 1,
      optRightLosesClothing1Hole: 45,
      optRightLosesClothing2Hole: 105,

      optLeftLosesClothing: true,
      optLeftLosesClothingCount: 2,
      optLeftLosesClothingPegs: 1,
      optLeftLosesClothing1Hole: 55,
      optLeftLosesClothing2Hole: 115,

      optTouchBreast: true,
      optTouchBreastCount: 2,
      optTouchBreastPegs: 1,
      optTouchBreast1Hole: 30,
      optTouchBreast2Hole: 90,

      optTouchGenitals: true,
      optTouchGenitalsCount: 2,
      optTouchGenitalsPegs: 1,
      optTouchGenitals1Hole: 60,
      optTouchGenitals2Hole: 118
    }
  },

  highHeat: {
    key: 'highHeat',
    name: 'High Heat',
    badge: '🔥 Intense Strip',
    icon: '🔥',
    description: 'Rapid strip penalties and zero mercy! Multiple strip tiles placed right before corners and skunk zones.',
    tags: ['Intense', 'Naked', 'Competitive'],
    dareSpaces: 20,
    config: {
      optLoseClothing: true,
      optLoseClothingCount: 4,
      optLoseClothingPegs: 1,
      optLoseClothing1Hole: 10,
      optLoseClothing2Hole: 40,
      optLoseClothing3Hole: 70,
      optLoseClothing4Hole: 100,

      optGainClothing: true,
      optGainClothingCount: 1,
      optGainClothingPegs: 1,
      optGainClothing1Hole: 60,

      optPickWhoLosesClothing: true,
      optPickWhoLosesClothingCount: 3,
      optPickWhoLosesClothingPegs: 1,
      optPickWhoLosesClothing1Hole: 20,
      optPickWhoLosesClothing2Hole: 50,
      optPickWhoLosesClothing3Hole: 80,

      optRightLosesClothing: true,
      optRightLosesClothingCount: 3,
      optRightLosesClothingPegs: 1,
      optRightLosesClothing1Hole: 25,
      optRightLosesClothing2Hole: 75,
      optRightLosesClothing3Hole: 105,

      optLeftLosesClothing: true,
      optLeftLosesClothingCount: 3,
      optLeftLosesClothingPegs: 1,
      optLeftLosesClothing1Hole: 30,
      optLeftLosesClothing2Hole: 85,
      optLeftLosesClothing3Hole: 115,

      optRemoveClothingChoice: true,
      optRemoveClothingChoiceCount: 3,
      optRemoveClothingChoicePegs: 1,
      optRemoveClothingChoice1Hole: 15,
      optRemoveClothingChoice2Hole: 65,
      optRemoveClothingChoice3Hole: 118,

      optTouchBreast: true,
      optTouchBreastCount: 2,
      optTouchBreastPegs: 1,
      optTouchBreast1Hole: 35,
      optTouchBreast2Hole: 95,

      optTouchGenitals: true,
      optTouchGenitalsCount: 1,
      optTouchGenitalsPegs: 1,
      optTouchGenitals1Hole: 110
    }
  },

  sensualGauntlet: {
    key: 'sensualGauntlet',
    name: 'Sensual Gauntlet',
    badge: '⏳ Timed Dares',
    icon: '⏳',
    description: 'An erotic challenge board heavily loaded with timed touch, kiss, lick, and genital dares at nearly every milestone.',
    tags: ['Erotic', 'Timed Dares', 'Intimate'],
    dareSpaces: 22,
    config: {
      optLoseClothing: true,
      optLoseClothingCount: 2,
      optLoseClothingPegs: 1,
      optLoseClothing1Hole: 10,
      optLoseClothing2Hole: 70,

      optPickWhoLosesClothing: true,
      optPickWhoLosesClothingCount: 2,
      optPickWhoLosesClothingPegs: 1,
      optPickWhoLosesClothing1Hole: 40,
      optPickWhoLosesClothing2Hole: 100,

      optTouchBreast: true,
      optTouchBreastCount: 3,
      optTouchBreastPegs: 1,
      optTouchBreast1Hole: 15,
      optTouchBreast2Hole: 45,
      optTouchBreast3Hole: 75,

      optLickBreast: true,
      optLickBreastCount: 3,
      optLickBreastPegs: 1,
      optLickBreast1Hole: 20,
      optLickBreast2Hole: 50,
      optLickBreast3Hole: 80,

      optSuckBreast: true,
      optSuckBreastCount: 2,
      optSuckBreastPegs: 1,
      optSuckBreast1Hole: 25,
      optSuckBreast2Hole: 85,

      optTouchGenitals: true,
      optTouchGenitalsCount: 3,
      optTouchGenitalsPegs: 1,
      optTouchGenitals1Hole: 30,
      optTouchGenitals2Hole: 60,
      optTouchGenitals3Hole: 90,

      optLickGenitals: true,
      optLickGenitalsCount: 2,
      optLickGenitalsPegs: 1,
      optLickGenitals1Hole: 35,
      optLickGenitals2Hole: 95,

      optSuckGenitals: true,
      optSuckGenitalsCount: 2,
      optSuckGenitalsPegs: 1,
      optSuckGenitals1Hole: 55,
      optSuckGenitals2Hole: 110,

      optFingerGenitals: true,
      optFingerGenitalsCount: 3,
      optFingerGenitalsPegs: 1,
      optFingerGenitals1Hole: 65,
      optFingerGenitals2Hole: 105,
      optFingerGenitals3Hole: 118
    }
  }
});

export class AdultPresets {
  static getStorage(storage = null) {
    if (storage) return storage;
    if (typeof window !== 'undefined' && window.appStorage) return window.appStorage;
    return appStorage;
  }

  static getPresets(target = 121) {
    const t = Number(target) || 121;
    if (t === 121) return ADULT_PRESETS_121;
    return AdultPresets.scalePresetsForTarget(ADULT_PRESETS_121, t);
  }

  static scalePresetsForTarget(presets, target) {
    const scale = (target - 1) / 120;
    const scaled = {};

    for (const [key, preset] of Object.entries(presets)) {
      const cfg = { ...preset.config };
      for (const [prop, val] of Object.entries(cfg)) {
        if (typeof val === 'number' && prop.endsWith('Hole')) {
          cfg[prop] = Math.min(target - 2, Math.max(2, Math.round(val * scale)));
        }
      }
      scaled[key] = {
        ...preset,
        config: cfg
      };
    }
    return scaled;
  }

  static computeTrackMetrics(items = [], target = 121) {
    const total = items.length;
    let stripSpaces = 0;
    let timedDares = 0;

    const timedKeys = new Set([
      'touchBreast', 'lickBreast', 'suckBreast',
      'touchGenitals', 'lickGenitals', 'suckGenitals', 'fingerGenitals'
    ]);

    const holes = [];
    for (const item of items) {
      if (item.hole != null) holes.push(item.hole);
      if (timedKeys.has(item.type)) timedDares++;
      else stripSpaces++;
    }

    holes.sort((a, b) => a - b);
    let avgSpacing = 0;
    if (holes.length > 1) {
      let sumDist = 0;
      for (let i = 1; i < holes.length; i++) {
        sumDist += (holes[i] - holes[i - 1]);
      }
      avgSpacing = Math.round(sumDist / (holes.length - 1));
    } else if (holes.length === 1) {
      avgSpacing = target;
    }

    return {
      totalDareSpaces: total,
      stripSpaces,
      timedDares,
      avgSpacing
    };
  }

  static applyPreset(presetKey, context = {}) {
    const target = (context.settings && context.settings.target) || (typeof window !== 'undefined' && window.settings && window.settings.target) || 121;
    const presets = AdultPresets.getPresets(target);
    const preset = presets[presetKey] || presets.standard;
    if (!preset) return false;

    const cfg = preset.config;
    const doc = context.document || (typeof document !== 'undefined' ? document : null);

    if (doc) {
      for (const [prop, val] of Object.entries(cfg)) {
        const el = doc.getElementById(prop);
        if (!el) continue;
        if (el.type === 'checkbox') {
          el.checked = Boolean(val);
          const toggleFn = (typeof window !== 'undefined') ? window[`toggle${prop.replace('opt', '')}Config`] : null;
          if (typeof toggleFn === 'function') toggleFn(el.checked);
        } else {
          el.value = String(val);
          el.dataset.customized = 'true';
        }
      }
      if (typeof window !== 'undefined' && typeof window.updateTrackItemRows === 'function') {
        const keys = [
          'loseClothing', 'gainClothing', 'pickWhoLosesClothing', 'rightLosesClothing', 'leftLosesClothing', 'removeClothingChoice',
          'touchBreast', 'lickBreast', 'suckBreast', 'touchGenitals', 'lickGenitals', 'suckGenitals', 'fingerGenitals'
        ];
        keys.forEach(k => window.updateTrackItemRows(k));
      }
    }

    if (context.settings || (typeof window !== 'undefined' && window.settings)) {
      const s = context.settings || window.settings;
      Object.assign(s, cfg);
      s.adultPreset = presetKey;
    }
    if (typeof window !== 'undefined' && typeof window.saveBrandSettings === 'function' && typeof window.readBrandSettings === 'function') {
      window.saveBrandSettings('adult', window.readBrandSettings('adult'));
    }
    return true;
  }

  static getCustomLayouts(storage = null) {
    const s = AdultPresets.getStorage(storage);
    try {
      const raw = s.getItem(ADULT_CUSTOM_LAYOUTS_KEY);
      if (!raw) return [];
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('[AdultPresets] Error loading custom layouts:', e);
      return [];
    }
  }

  static saveCustomLayout(layout, storage = null, isImport = false) {
    if (!layout || typeof layout !== 'object') throw new Error('Invalid layout object');
    const s = AdultPresets.getStorage(storage);
    const layouts = AdultPresets.getCustomLayouts(s);

    const now = new Date().toISOString();
    const sanitized = LayoutSanitizer.sanitizeLayout(layout, 'adult', {
      regenerateId: isImport || !layout.id
    });
    sanitized.metrics = AdultPresets.computeTrackMetrics(sanitized.items, sanitized.target);

    let targetLayout = null;
    if (!isImport && layout.id) {
      const idx = layouts.findIndex(l => l.id === sanitized.id);
      if (idx !== -1) {
        targetLayout = {
          ...layouts[idx],
          ...sanitized,
          updatedAt: now
        };
        layouts[idx] = targetLayout;
      }
    }

    if (!targetLayout) {
      targetLayout = sanitized;
      layouts.unshift(targetLayout);
    }

    const capped = LayoutSanitizer.enforceStorageQuota(layouts, MAX_CUSTOM_LAYOUTS);

    if (s) {
      try {
        s.setItem(ADULT_CUSTOM_LAYOUTS_KEY, JSON.stringify(capped));
      } catch (e) {
        console.error('[AdultPresets] Failed to save custom layouts:', e);
        throw new Error('Failed to save layout to storage: Quota exceeded or storage unavailable.');
      }
    }
    return targetLayout;
  }

  static renameCustomLayout(layoutId, newName, storage = null) {
    if (!layoutId || !newName || !newName.trim()) return false;
    const s = AdultPresets.getStorage(storage);
    const layouts = AdultPresets.getCustomLayouts(s);
    const layout = layouts.find(l => l.id === layoutId);
    if (!layout) return false;

    layout.name = newName.trim();
    layout.updatedAt = new Date().toISOString();
    s.setItem(ADULT_CUSTOM_LAYOUTS_KEY, JSON.stringify(layouts));
    return true;
  }

  static deleteCustomLayout(layoutId, storage = null) {
    if (!layoutId) return false;
    const s = AdultPresets.getStorage(storage);
    const layouts = AdultPresets.getCustomLayouts(s);
    const filtered = layouts.filter(l => l.id !== layoutId);
    if (filtered.length === layouts.length) return false;
    s.setItem(ADULT_CUSTOM_LAYOUTS_KEY, JSON.stringify(filtered));
    return true;
  }

  static duplicateCustomLayout(layoutId, storage = null) {
    if (!layoutId) return null;
    const s = AdultPresets.getStorage(storage);
    const layouts = AdultPresets.getCustomLayouts(s);
    const original = layouts.find(l => l.id === layoutId);
    if (!original) return null;

    const copy = {
      ...original,
      id: undefined,
      name: `${original.name} (Copy)`
    };
    return AdultPresets.saveCustomLayout(copy, s);
  }

  static exportLayout(layoutId = null, storage = null) {
    const s = AdultPresets.getStorage(storage);
    const layouts = AdultPresets.getCustomLayouts(s);
    if (layoutId) {
      const single = layouts.find(l => l.id === layoutId);
      const prepared = LayoutSanitizer.prepareForExport(single || null);
      return JSON.stringify(prepared, null, 2);
    }
    const prepared = LayoutSanitizer.prepareForExport(layouts);
    return JSON.stringify(prepared, null, 2);
  }

  static importLayout(jsonStr, storage = null) {
    const parsed = LayoutSanitizer.parseJsonSafely(jsonStr);
    const s = AdultPresets.getStorage(storage);
    if (Array.isArray(parsed)) {
      if (parsed.length > 20) {
        throw new Error('Cannot import more than 20 layouts at once.');
      }
      let lastSaved = null;
      for (const item of parsed) {
        lastSaved = AdultPresets.saveCustomLayout(item, s, true);
      }
      return lastSaved;
    }
    return AdultPresets.saveCustomLayout(parsed, s, true);
  }
}

if (typeof window !== 'undefined') {
  window.AdultPresets = AdultPresets;
  window.ADULT_PRESETS_121 = ADULT_PRESETS_121;
}
